import { CopyOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Modal, Space, Typography } from 'antd';
import { useState } from 'react';
import { getBrandBaseUrl } from '../../../common/utils/getBrandDomain';

const { Text } = Typography;

/**
 * Ready-to-run commands. --server is always included, so the same command
 * works on modelpark.app, on white-label sites, and for CLIs that were
 * pointed at another server before.
 */
export function buildTokenCommands(baseUrl: string, token: string) {
    return {
        login: `modelpark login --server ${baseUrl} --token ${token}`,
        ci: `export MODELPARK_API_URL=${baseUrl}\nexport MODELPARK_TOKEN=${token}`,
    };
}

function CopyField({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const style = { fontFamily: 'monospace', fontSize: 13 };
    return (
        <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {label}
            </Text>
            <Space.Compact style={{ width: '100%' }}>
                {multiline ? (
                    <Input.TextArea value={value} readOnly autoSize style={style} />
                ) : (
                    <Input value={value} readOnly style={style} />
                )}
                <Button icon={<CopyOutlined />} onClick={copy}>
                    {copied ? 'Copied' : 'Copy'}
                </Button>
            </Space.Compact>
        </div>
    );
}

interface Props {
    token: string;
    open: boolean;
    onClose: () => void;
}

export default function TokenRevealModal({ token, open, onClose }: Props) {
    const commands = buildTokenCommands(getBrandBaseUrl(), token);

    return (
        <Modal
            title="API Token Created"
            open={open}
            onOk={onClose}
            onCancel={onClose}
            okText="Done"
            cancelButtonProps={{ style: { display: 'none' } }}
            // The token is shown only once: a stray click or Esc must not lose it.
            maskClosable={false}
            keyboard={false}
            width={640}
        >
            <Alert type="warning" message="Save this token now. It won't be shown again." style={{ marginBottom: 16 }} />
            <CopyField label="Token" value={token} />
            <CopyField label="Log in with the CLI" value={commands.login} />
            <CopyField label="CI / Docker" value={commands.ci} multiline />
        </Modal>
    );
}
